import { Box } from "@mui/material"
import React, {
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react"
import useApi from "modules/arcadeGames"
import { useActiveContentContext } from "contexts/ActiveContentContext"
import { useAuthContext } from "./AuthContext"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { routes } from "../common/navigationRoutes"
import {
  CONTENT_TYPES,
  FILE_TYPES,
} from "pages/student/activity/common/constants"
import { useGlobalAlert } from "./GlobalAlertContext"
import useSound from "hooks/useSound"
import { gamesSounds } from "utils/sounds"
import StartGame from "pages/games/components/StartGame"
import { CONTENT_REFERENCE_TYPES, GAMES } from "../pages/games/common/constants"
import { useStudentHub } from "./StudentHubContext"
import { getScreenId } from "common/functions"

export const GamesContext = React.createContext()

export const useGamesContext = () => {
  const context = useContext(GamesContext)
  if (context === undefined) {
    throw new Error("useGamesContext must be used within a GamesProvider")
  }
  return context
}

const processFixItRushData = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (!Array.isArray(list)) return []

  // 1. Get all questions (contentTypeId === 3 and isPrimaryContent === true)
  const questions = list.filter(
    (item) =>
      item.contentTypeId === CONTENT_TYPES.QUESTION && item.isPrimaryContent
  )

  // 2. Map each question to the mock structure
  return questions.map((q, qIndex) => {
    const qSequence = q.sequence
    const qContentId = q.contentId
    const qExerciseId = q.exerciseId
    const qGameSessionContentId = q.gameSessionContentId
    const qAudio = q.files.find(
      (f) => f.fileTypeId === FILE_TYPES.AUDIO
    )?.fileURL

    // 3. Find options for this question (contentTypeId === 1 or 4)
    // linked by contentReference === question.contentId
    const optionsForQuestion = list.filter(
      (item) =>
        (item.contentTypeId === CONTENT_TYPES.WORD ||
          item.contentTypeId === CONTENT_TYPES.ANSWER) &&
        item.contentReference === qContentId &&
        item.exerciseId === qExerciseId
    )
    // Convert options to mock structure
    const options = optionsForQuestion.map((opt, optIndex) => ({
      id: String.fromCharCode(65 + optIndex), // "A", "B", "C", "D"
      text: (opt.text || "").trim(),
      isAnswer: opt.contentTypeId === CONTENT_TYPES.ANSWER || opt.isAnswer,
      contentId: opt.contentId,
      gameSessionContentId: opt.gameSessionContentId,
      sequence: opt.sequence,
    }))

    // Find the correct sentence
    const correctOpt = optionsForQuestion.find(
      (opt) => opt.contentTypeId === CONTENT_TYPES.ANSWER || opt.isAnswer
    )
    const correctSentence = correctOpt ? (correctOpt.text || "").trim() : ""

    // 4. Find the explanation for this question (contentTypeId === 2)
    // matching the question's sequence
    const explanationItem = list.find(
      (item) =>
        item.contentTypeId === CONTENT_TYPES.EXPLANATION &&
        item.sequence === qSequence &&
        item.exerciseId === qExerciseId
    )
    const explanationText = explanationItem ? explanationItem.text || "" : ""

    // Find instruction (contentTypeId === 13) matching sequence
    const instructionItem = list.find(
      (item) =>
        item.contentTypeId === CONTENT_TYPES.INSTRUCTIONS &&
        item.sequence === qSequence &&
        item.exerciseId === qExerciseId
    )
    const instructionText = instructionItem
      ? (instructionItem.text || "").trim()
      : ""

    return {
      id: qSequence || qIndex + 1,
      question: (q.text || "").trim(),
      correctSentence,
      explanation: explanationText,
      options: shuffleArray(options),
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId: qGameSessionContentId,
      audioURL: qAudio,
      instruction: instructionText,
      exerciseId: qExerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

const processWordsData = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (!Array.isArray(list)) return []

  const questions = list.filter(
    (item) => Number(item.contentTypeId) === 1 && item.isPrimaryContent === true
  )

  return questions.map((q, qIndex) => {
    const qSequence = Number(q.sequence)
    const qContentId = Number(q.contentId)
    const qGameSessionContentId = q.gameSessionContentId
    const qExerciseId = q.exerciseId
    const qAudio = q.files.find(
      (f) => f.fileTypeId === FILE_TYPES.AUDIO
    )?.fileURL

    // 4. Find the explanation for this question (contentTypeId === 2)
    // matching the question's sequence
    const explanationItem = list.find(
      (item) =>
        Number(item.contentTypeId) === 2 &&
        Number(item.sequence) === qSequence &&
        item.exerciseId === qExerciseId
    )
    const explanationText = explanationItem ? explanationItem.text || "" : ""
    const imageURL = explanationItem?.files?.find(
      (f) => f.fileTypeId === FILE_TYPES.IMAGE
    )?.fileURL

    return {
      id: qSequence || qIndex + 1,
      question: (q.text || "").trim(),
      correctSentence: q.text,
      explanation: explanationText,
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId: qGameSessionContentId,
      audioURL: qAudio,
      imageURL,
      translatedText: explanationItem?.translatedText || "",
      gameUserFavoriteContentId:
        q.gameUserFavoriteContentId ||
        explanationItem?.gameUserFavoriteContentId ||
        q.metadata?.gameUserFavoriteContentId,
      isFavorite: q.isFavorite || explanationItem?.isFavorite,
      exerciseId: qExerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

// Fisher-Yates shuffle — returns a new array
const shuffleArray = (arr) => {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

const processSentenceScrambleData = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (!Array.isArray(list)) return []

  // Words: contentTypeId=14 and isPrimaryContent=true, ordered by sequence
  const words = list
    .filter(
      (item) =>
        Number(item.contentTypeId) === 14 &&
        (item.isPrimaryContent === true ||
          String(item.isPrimaryContent).toLowerCase() === "true")
    )
    .sort((a, b) => Number(a.sequence) - Number(b.sequence))

  if (words.length === 0) return []

  // Correct sentence = words in sequence order
  const correctSentence = words.map((w) => (w.text || "").trim()).join(" ")

  // Word objects preserve correct position (0-based index = correct order)
  const wordObjects = words.map((w, i) => ({
    word: (w.text || "").trim(),
    sequence: w.sequence,
    contentId: w.contentId,
    gameSessionContentId: w.gameSessionContentId,
    isLastWord: i === words.length - 1,
    exerciseId: w.exerciseId,
    moduleActivityId: w?.moduleActivityId || 0,
  }))

  // Shuffled copy for display
  const scrambledWords = shuffleArray(wordObjects)

  return [
    {
      id: 1,
      correctSentence,
      wordObjects,
      scrambledWords,
    },
  ]
}

const processScenarioSnapExercises = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (list.length === 0) return []
  const questions = list.filter((item) => item.isPrimaryContent === true)
  const allOptions = list.filter(
    (item) => item.contentTypeId === CONTENT_TYPES.RELATE_B
  )
  return questions.map((q, qIndex) => {
    const qSequence = q.sequence
    const qContentId = q.contentId
    const qGameSessionContentId = q.gameSessionContentId
    const qExerciseId = q.exerciseId
    const correctOpt = allOptions.find(
      (opt) => opt.sequence === qSequence && opt.exerciseId === qExerciseId
    )
    const incorrectOpts = allOptions.filter((opt) => opt.sequence !== qSequence)
    const selectedIncorrect = shuffleArray(incorrectOpts).slice(0, 3)
    const combined = correctOpt
      ? [correctOpt, ...selectedIncorrect]
      : selectedIncorrect

    const options = combined.map((opt, optIndex) => ({
      id: String.fromCharCode(65 + optIndex), // "A", "B", "C", "D"
      text: opt.text || "".trim(),
      isAnswer: opt.sequence === qSequence && opt.exerciseId === qExerciseId,
      contentId: opt.contentId,
      gameSessionContentId: opt.gameSessionContentId,
      sequence: opt.sequence,
    }))
    const shuffledOptions = shuffleArray(options)
    const correctSentence = correctOpt ? (correctOpt.text || "").trim() : ""
    const explanationItem = list.find(
      (item) =>
        item.contentTypeId === CONTENT_TYPES.EXPLANATION &&
        item.sequence === qSequence &&
        item.exerciseId === qExerciseId
    )
    const explanationText = explanationItem ? explanationItem.text || "" : ""

    return {
      id: qSequence || qIndex + 1,
      question: (q.text || "").trim(),
      correctSentence,
      explanation: explanationText,
      options: shuffledOptions,
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId: qGameSessionContentId,
      exerciseId: qExerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

const processMemoryPalaceData = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (!Array.isArray(list)) return []

  const primaryContentItems = list.filter(
    (item) => item.isPrimaryContent === true
  )

  const words = primaryContentItems.map((item, index) => {
    const qSequence = item.sequence
    const qContentId = item.contentId
    const qExerciseId = item.exerciseId
    const qGameSessionContentId = item.gameSessionContentId
    const audioURL = item.files?.find(
      (f) => f.fileTypeId === FILE_TYPES.AUDIO
    )?.fileURL

    const explanationItem = list.find(
      (x) =>
        Number(x.contentTypeId) === 2 &&
        Number(x.sequence) === qSequence &&
        x.exerciseId === qExerciseId
    )
    const imageURL = explanationItem?.files?.find(
      (f) => f.fileTypeId === FILE_TYPES.IMAGE
    )?.fileURL

    return {
      word: (item.text || "").trim(),
      translation: (item.translatedText || "").trim(),
      audioURL,
      imageURL,
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId: qGameSessionContentId,
      exerciseId: qExerciseId,
      moduleActivityId: item?.moduleActivityId || 0,
    }
  })

  const limitedWords = words.slice(0, 9)

  if (limitedWords.length === 0) return []

  return [
    {
      id: 1,
      gameSessionContentId: limitedWords[0]?.gameSessionContentId || 0,
      words: limitedWords,
    },
  ]
}

const processConversationLabExercises = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (list.length === 0) return []

  // 1. Get all questions (isPrimaryContent === true or "true")
  const questions = list.filter(
    (item) =>
      item.isPrimaryContent === true ||
      String(item.isPrimaryContent).toLowerCase() === "true"
  )

  // 2. Get all option candidates (contentTypeId === 8)
  const allOptions = list.filter((item) => Number(item.contentTypeId) === 8)

  return questions.map((q, qIndex) => {
    const qSequence = Number(q.sequence)
    const qContentId = Number(q.contentId)
    const qExerciseId = q.exerciseId
    const qGameSessionContentId = q.gameSessionContentId
    const qAudio = q.files?.find(
      (f) => Number(f.fileTypeId) === FILE_TYPES.AUDIO
    )?.fileURL
    const correctOpt = allOptions.find(
      (opt) =>
        Number(opt.sequence) === qSequence && opt.exerciseId === qExerciseId
    )
    const correctSentence = correctOpt ? (correctOpt.text || "").trim() : ""
    const instrucctions = list.find(
      (item) =>
        Number(item.contentTypeId) === 13 &&
        Number(item.sequence) === qSequence &&
        item.exerciseId === qExerciseId
    )
    const instrucction = instrucctions ? instrucctions.text || "" : ""
    const instrucctionImage = instrucctions?.files?.find(
      (f) => Number(f.fileTypeId) === FILE_TYPES.IMAGE
    )?.fileURL

    return {
      id: qSequence || qIndex + 1,
      question: (q.text || "").trim(),
      instrucction,
      questionTranslation: (q.translatedText || "").trim(),
      correctSentence,
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId: qGameSessionContentId,
      audioURL: qAudio,
      imageURL: instrucctionImage,
      answer: correctSentence,
      answerTranslation: (correctOpt?.translatedText || "").trim(),
      audioAnswerURL: correctOpt?.files?.find(
        (f) => f.fileTypeId === FILE_TYPES.AUDIO
      )?.fileURL,
      answerImageURL: correctOpt?.files?.find(
        (f) => f.fileTypeId === FILE_TYPES.IMAGE
      )?.fileURL,
      exerciseId: qExerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

const processTranslationDuelExercises = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (list.length === 0) return []

  // 1. Get all questions (isPrimaryContent === true and contentTypeId === 1)
  const questions = list.filter(
    (item) =>
      (item.isPrimaryContent === true ||
        String(item.isPrimaryContent).toLowerCase() === "true") &&
      Number(item.contentTypeId) === 1
  )

  const allQuestions = list.filter(
    (item) => Number(item.contentTypeId) === CONTENT_TYPES.WORD
  )

  // get all possible options
  const allOptions = list.filter(
    (item) => Number(item.contentTypeId) === CONTENT_TYPES.EXPLANATION
  )

  return questions.map((q, qIndex) => {
    const qSequence = Number(q.sequence)
    const qExerciseId = q.exerciseId
    const qContentId = Number(q.contentId)
    const qGameSessionContentId = q.gameSessionContentId

    const explanationItem = list.find(
      (item) =>
        Number(item.contentTypeId) === CONTENT_TYPES.EXPLANATION &&
        Number(item.sequence) === qSequence &&
        item.exerciseId === qExerciseId
    )

    // Correct option
    const correctOpt = {
      text: (explanationItem.translatedText || "").trim(),
      isAnswer: true,
      contentId: explanationItem.contentId,
      gameSessionContentId: explanationItem.gameSessionContentId,
      sequence: explanationItem.sequence,
      originalText: q.text || "",
    }

    // Incorrect options: 3 random translations from other questions
    const otherOptions = allOptions.filter(
      (item) => Number(item.sequence) !== qSequence
    )
    const shuffledOthers = shuffleArray(otherOptions)
    const selectedIncorrect = shuffledOthers.slice(0, 3).map((other) => {
      const origen = allQuestions.find(
        (item) => item.sequence === other.sequence
      )
      return {
        text: (other.translatedText || "").trim(),
        originalText: origen?.text || "",
        isAnswer: false,
        contentId: Number(other.contentId),
        gameSessionContentId: other.gameSessionContentId,
        sequence: Number(other.sequence),
      }
    })

    const combined = [correctOpt, ...selectedIncorrect]

    // Map options and shuffle them so correct one is randomized
    const options = combined.map((opt, optIndex) => ({
      id: opt.contentId,
      text: opt.text,
      isAnswer: opt.isAnswer,
      contentId: opt.contentId,
      gameSessionContentId: opt.gameSessionContentId,
      sequence: opt.sequence,
      originalText: opt.originalText,
    }))

    const shuffledOptions = shuffleArray(options)

    return {
      id: qSequence || qIndex + 1,
      question: (q.text || "").trim(),
      correctSentence: correctOpt.text,
      explanation: "",
      options: shuffledOptions,
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId: qGameSessionContentId,
      exerciseId: qExerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

const processPhraseBuilderData = (rawList) => {
  const list = Array.isArray(rawList)
    ? rawList
    : rawList?.gameSessionContents || rawList?.contents || []
  if (!Array.isArray(list)) return []

  const getFiles = (item) => {
    const files = item?.files || []
    const audioURL =
      files.find((f) => Number(f.fileTypeId) === FILE_TYPES.AUDIO)?.fileURL ||
      ""
    const imageURL =
      files.find((f) => Number(f.fileTypeId) === FILE_TYPES.IMAGE)?.fileURL ||
      ""
    return { audioURL, imageURL }
  }

  // 1. Identify all questions & explanation candidates
  const type1Items = list.filter((item) => Number(item.contentTypeId) === 1)
  const hasType1Partner = (exerciseId) => {
    if (exerciseId === undefined || exerciseId === null) return false
    return type1Items.some(
      (item) => Number(item.exerciseId) === Number(exerciseId)
    )
  }

  // Find questions
  const questions = list.filter((item) => {
    const cId = Number(item.contentTypeId)
    if (cId === 3) return true
    if (cId === 9) {
      return hasType1Partner(item.exerciseId)
    }
    return false
  })

  // Find explanations
  const explanations = list.filter((item) => {
    const cId = Number(item.contentTypeId)
    if (cId === 9) {
      return !hasType1Partner(item.exerciseId)
    }
    return false
  })

  // 2. Map questions to expected structure
  return questions.map((q, qIndex) => {
    const qSequence = q.sequence
    const qContentId = q.contentId
    const qGameSessionContentId = q.gameSessionContentId
    const qExerciseId = q.exerciseId
    const qText = (q.text || "").trim()
    const qFiles = getFiles(q)
    let aGameSessionContentId = 0

    // Find options
    let optionsForQuestion = []
    const qCId = q.contentTypeId
    if (qCId === CONTENT_TYPES.QUESTION) {
      optionsForQuestion = list.filter(
        (item) =>
          item.contentTypeId === CONTENT_TYPES.ANSWER &&
          item.exerciseId === qExerciseId
      )
    }
    if (qCId === CONTENT_TYPES.TEXT) {
      optionsForQuestion = list.filter(
        (item) =>
          item.contentTypeId === CONTENT_TYPES.WORD &&
          item.exerciseId === qExerciseId
      )
      if (optionsForQuestion.length > 0) {
        aGameSessionContentId = optionsForQuestion?.[0]?.gameSessionContentId
      }
    }

    // Map options
    const options = optionsForQuestion.map((opt, optIndex) => {
      const optFiles = getFiles(opt)

      let isAnswer = false
      if (qCId === CONTENT_TYPES.QUESTION) {
        isAnswer = opt.isAnswer === true
      } else {
        isAnswer = opt.contentTypeId === CONTENT_TYPES.WORD
      }

      return {
        id: String.fromCharCode(65 + optIndex), // "A", "B", "C", "D"
        text: (opt.text || "").trim(),
        isAnswer,
        contentId: opt.contentId,
        gameSessionContentId: opt.gameSessionContentId,
        sequence: opt.sequence,
        audioURL: optFiles.audioURL,
        imageURL: optFiles.imageURL,
      }
    })

    // Find correct sentence
    const correctOpt = options.find((opt) => opt.isAnswer)
    const correctSentence = correctOpt ? correctOpt.text : ""

    // Find closest explanation
    let explanationText = ""
    let explanationAudioURL = ""
    let explanationImageURL = ""

    if (explanations.length > 0) {
      const qOrigIndex = list.indexOf(q)
      let closestExp = null
      let minDistance = Infinity

      for (const exp of explanations) {
        const expOrigIndex = list.indexOf(exp)
        const distance = Math.abs(qOrigIndex - expOrigIndex)
        if (distance < minDistance) {
          minDistance = distance
          closestExp = exp
        }
      }

      if (closestExp) {
        explanationText = (closestExp.text || "").trim()
        const expFiles = getFiles(closestExp)
        explanationAudioURL = expFiles.audioURL
        explanationImageURL = expFiles.imageURL
      }
    }

    return {
      id: qSequence || qIndex + 1,
      question: qText,
      correctSentence,
      explanation:
        explanationText?.trim()?.length === 0 &&
        explanationAudioURL?.trim()?.length === 0 &&
        explanationImageURL?.trim()?.length === 0
          ? null
          : {
              text: explanationText,
              audioURL: explanationAudioURL,
              imageURL: explanationImageURL,
            },
      options: shuffleArray(options),
      contentId: qContentId,
      sequence: qSequence,
      gameSessionContentId:
        q.contentTypeId === CONTENT_TYPES.TEXT
          ? aGameSessionContentId
          : qGameSessionContentId,
      audioURL: qFiles.audioURL,
      imageURL: qFiles.imageURL,
      isPair: q.contentTypeId === CONTENT_TYPES.TEXT,
      exerciseId: qExerciseId,
      moduleActivityId: q?.moduleActivityId || 0,
    }
  })
}

const selectProcessor = (gameId, contents) => {
  if (gameId === 3) return processPhraseBuilderData(contents)
  if (gameId === 4) return processWordsData(contents)
  if (gameId === 5) return processMemoryPalaceData(contents)
  if (gameId === 6 || gameId === 16 || gameId === 17) {
    const processed = processMemoryPalaceData(contents)
    if (processed.length > 0) {
      processed[0].words = processed[0].words.slice(0, 8)
    }
    return processed
  }
  if (gameId === 8) return processSentenceScrambleData(contents)
  if (gameId === 9) return [{ contents }]
  if (gameId === 10) return processTranslationDuelExercises(contents)
  if (gameId === 11) return processScenarioSnapExercises(contents)
  if (gameId === 12) return processConversationLabExercises(contents)
  return processFixItRushData(contents)
}

const GamesProvider = ({ children, sx }) => {
  const { userData, audioSettings, accountType, selectedGroupData } =
    useAuthContext()
  const [currentGame, setCurrentGame] = useState({
    colors: ["#fff", "#fff"],
  })
  const [gameSessionData, setGameSessionData] = useState(null)
  const [exerciseContentIndex, setExerciseContentIndex] = useState(0)
  const [exercisesContent, setExercisesContent] = useState([])
  const [answers, setAnswers] = useState([])
  const [isLoadingGameContent, setIsLoadingGameContent] = useState(false)
  const [isGameFinished, setIsGameFinished] = useState(false)
  const [countdownCompleted, setCountdownCompleted] = useState(false)
  const [playerStats, setPlayerStats] = useState({
    place: 0,
    totalGamesPlayed: 0,
    totalPoints: 0,
  })
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { setActiveContent } = useActiveContentContext()
  const {
    setIndividualGameSession,
    setGameSessionAnswer,
    setGameSessionStatus,
    setGameSessionNextContent,
    getGameSessionById,
    getCategories,
    getGames,
    retryIndividualGameSession,
  } = useApi()
  const { setSessionActivityLog } = useStudentHub()
  const { showAlert } = useGlobalAlert()
  const [playGoodAnswer] = useSound(gamesSounds.goodAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })
  const [playWrongAnswer] = useSound(gamesSounds.wrongAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
  })

  // Reset countdown completed flag when returning to the games list
  useEffect(() => {
    const path = location.pathname.toLowerCase()
    const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path
    const isGamesList = normalizedPath.endsWith(routes.NEW_GAMES)

    if (isGamesList) {
      setCountdownCompleted(false)
    }
  }, [location.pathname])

  useEffect(() => {
    const currentExercise = exercisesContent[exerciseContentIndex]
    if (
      currentExercise?.exerciseId > 0 &&
      currentExercise?.moduleActivityId > 0
    ) {
      setActiveContent({
        relatedId: currentExercise?.moduleActivityId,
        subRelatedId: currentExercise?.exerciseId,
        screen: "games",
      })
    }
  }, [exercisesContent, exerciseContentIndex])

  const fetchGameById = useCallback(
    async (gameId) => {
      try {
        const categories = await getCategories()
        for (const category of categories) {
          const gamesList = await getGames({
            categoryId: category.gameCategoryId,
          })
          const found = gamesList.find((g) => g.gameId === gameId)
          if (found) {
            return {
              ...found,
              id: found.gameId,
              title: found.name,
              time: found.durationLabel,
              xp: found.maxPoints,
              color: found.metadata?.colors?.primary,
              colors: [
                found.metadata?.colors?.primary,
                found.metadata?.colors?.secondary,
              ],
              icon: found.iconEmoji,
            }
          }
        }
      } catch (e) {
        console.error("Error fetching game by ID:", e)
      }
      return null
    },
    [getCategories, getGames]
  )

  const formatOnlyAnswered = useCallback(
    (answersFromPlayer = [], processed = []) => {
      if (
        gameSessionData?.gameId === GAMES.SENTENCE_SCRAMBLE ||
        currentGame?.id === GAMES.SENTENCE_SCRAMBLE
      ) {
        return answersFromPlayer.map((ans) => {
          const nestedAns = ans.answer?.[0] || ans.Answer?.[0]
          const metaStr =
            nestedAns?.Metadata ||
            nestedAns?.metadata ||
            ans.metadata ||
            ans.Metadata

          const isCorrectVal =
            ans.isCorrect !== undefined
              ? ans.isCorrect
              : nestedAns?.Status !== undefined
              ? nestedAns.Status
              : ans.IsCorrect || false

          return {
            ...ans,
            gameSessionContentId:
              ans.gameSessionContentId || ans.GameSessionContentId,
            metadata: metaStr,
            isCorrect: isCorrectVal,
            correctAnswer: ans.correctAnswer || "",
            userSelected: ans.userSelected || ans.UserSelected || "",
            audioURL: ans.audioURL || "",
            explanation: "",
          }
        })
      }

      const wordsList = processed[0]?.words || []

      if (wordsList.length > 0) {
        return answersFromPlayer.map((ans) => {
          const word = wordsList.find(
            (w) => w.gameSessionContentId === ans.gameSessionContentId
          )
          return {
            ...ans,
            isCorrect: ans.isCorrect || false,
            correctAnswer: word?.word || "",
            audioURL: word?.audioURL,
            userSelected: ans.isCorrect ? word?.word : "",
            explanation: "",
          }
        })
      } else {
        return answersFromPlayer.map((ans) => {
          const exercise = processed.find(
            (item) => item.gameSessionContentId === ans.gameSessionContentId
          )
          let playerAnswer = ""
          const rawAnsList = ans.answer || []
          const optionAnswer = rawAnsList.length > 0 ? rawAnsList[0] : null
          if (exercise) {
            if (exercise.options?.length > 1) {
              if (optionAnswer) {
                const optId =
                  optionAnswer.gameSessionContentId ||
                  optionAnswer.GameSessionContentId
                const playerSelectedOption = exercise.options.find(
                  (opt) => opt.gameSessionContentId === optId
                )
                playerAnswer = playerSelectedOption?.text || ""
              }
            } else {
              if (optionAnswer) {
                playerAnswer = optionAnswer.Answer || optionAnswer.answer || ""
              }
            }
          }
          return {
            ...ans,
            isCorrect: ans.isCorrect || false,
            correctAnswer: exercise?.correctSentence || "",
            audioURL: exercise?.audioURL || "",
            userSelected: playerAnswer || "",
            explanation:
              exercise && typeof exercise.explanation === "string"
                ? exercise.explanation
                : exercise?.explanation?.text || "",
          }
        })
      }
    },
    [gameSessionData, currentGame]
  )

  const formatPlayerAnswers = useCallback(
    (answersFromPlayer = [], processed = []) => {
      console.log("answersFromPlayer", answersFromPlayer)
      console.log("processed", processed)

      if (
        gameSessionData?.gameId === GAMES.SENTENCE_SCRAMBLE ||
        currentGame?.id === GAMES.SENTENCE_SCRAMBLE
      ) {
        const allFormattedWords = []
        processed.forEach((exercise) => {
          const words = exercise.wordObjects || []
          const wordAnswersMap = {}
          const answerForWordMap = {}

          words.forEach((w) => {
            const ans = [...answersFromPlayer]
              .reverse()
              .find(
                (a) =>
                  a.gameSessionContentId === w.gameSessionContentId ||
                  a.GameSessionContentId === w.gameSessionContentId
              )
            if (ans) {
              const nestedAns = ans.answer?.[0] || ans.Answer?.[0]
              const metaStr =
                nestedAns?.Metadata ||
                nestedAns?.metadata ||
                ans.metadata ||
                ans.Metadata
              try {
                const meta =
                  typeof metaStr === "string" ? JSON.parse(metaStr) : metaStr
                const pos = meta?.placedPosition
                if (pos !== undefined && pos >= 0) {
                  wordAnswersMap[pos] = w
                  answerForWordMap[pos] = ans
                }
              } catch (e) {
                console.error("Error parsing metadata for sentence scramble", e)
              }
            }
          })

          words.forEach((w, index) => {
            const placedWord = wordAnswersMap[index]
            const ans = answerForWordMap[index]
            const nestedAns = ans?.answer?.[0] || ans?.Answer?.[0]
            const isCorrectValue = ans
              ? ans.isCorrect !== undefined
                ? ans.isCorrect
                : nestedAns?.Status !== undefined
                ? nestedAns.Status
                : ans.IsCorrect || false
              : false

            allFormattedWords.push({
              ...ans,
              isCorrect: isCorrectValue,
              correctAnswer: w.word,
              userSelected:
                ans?.userSelected ||
                ans?.UserSelected ||
                (placedWord ? placedWord.word : ""),
              audioURL: w.audioURL,
              explanation: "",
            })
          })
        })
        return allFormattedWords
      }

      let validWordIds = null
      for (const ans of answersFromPlayer) {
        if (ans?.metadata) {
          try {
            const meta =
              typeof ans.metadata === "string"
                ? JSON.parse(ans.metadata)
                : ans.metadata
            if (Array.isArray(meta?.validWords)) {
              validWordIds = meta.validWords
              break
            }
          } catch (e) {
            console.error("Error parsing general metadata", e)
          }
        }
      }

      // Find the words list if this is a word-based game (e.g. Word Hunt, Memory Palace)
      let wordsList = processed[0]?.words || []
      if (validWordIds && wordsList.length > 0) {
        wordsList = wordsList.filter((word) =>
          validWordIds.includes(word.gameSessionContentId)
        )
      }

      if (wordsList.length > 0) {
        return wordsList.map((word) => {
          const matchingAns = answersFromPlayer.find(
            (ans) => ans.gameSessionContentId === word.gameSessionContentId
          )

          return {
            ...matchingAns,
            isCorrect: matchingAns?.isCorrect || false,
            correctAnswer: word?.word || "",
            audioURL: word?.audioURL,
            userSelected: matchingAns?.isCorrect ? word?.word : "",
            explanation: "",
          }
        })
      } else {
        let exercisesToFormat = processed
        if (validWordIds) {
          exercisesToFormat = processed.filter((exercise) =>
            validWordIds.includes(exercise.gameSessionContentId)
          )
        }

        return exercisesToFormat.map((exercise) => {
          const matchingAns = answersFromPlayer.find(
            (ans) => ans.gameSessionContentId === exercise.gameSessionContentId
          )
          console.log("matchingAns", matchingAns)
          let playerAnswer = ""
          const rawAnsList = matchingAns?.answer || []
          const optionAnswer = rawAnsList.length > 0 ? rawAnsList[0] : null
          if (exercise.options?.length > 1) {
            if (optionAnswer) {
              const optId =
                optionAnswer.gameSessionContentId ||
                optionAnswer.GameSessionContentId
              const playerSelectedOption = exercise.options.find(
                (opt) => opt.gameSessionContentId === optId
              )
              playerAnswer = playerSelectedOption?.text || ""
            }
          } else {
            if (optionAnswer) {
              playerAnswer = optionAnswer.Answer || optionAnswer.answer || ""
            }
          }

          const isConversationLab =
            exercise?.audioAnswerURL !== undefined ||
            gameSessionData?.gameId === 12
          let userSelected = playerAnswer || ""
          if (isConversationLab) {
            userSelected = ""
            if (matchingAns?.metadata) {
              try {
                const meta =
                  typeof matchingAns.metadata === "string"
                    ? JSON.parse(matchingAns.metadata)
                    : matchingAns.metadata
                userSelected = meta?.transcript || ""
              } catch (e) {
                console.error("Error parsing metadata", e)
              }
            }
          }

          return {
            ...matchingAns,
            isCorrect: matchingAns?.isCorrect || false,
            correctAnswer:
              exercise?.correctSentence ||
              exercise?.options?.find((opt) => opt.isAnswer)?.text ||
              "",
            audioURL: isConversationLab
              ? exercise?.audioAnswerURL || ""
              : exercise?.audioURL || "",
            userSelected,
            explanation:
              typeof exercise.explanation === "string"
                ? exercise.explanation
                : exercise.explanation?.text || "",
          }
        })
      }
    },
    [gameSessionData]
  )

  const loadSession = useCallback(
    async (sessionId) => {
      try {
        const response = await getGameSessionById({ gameSessionId: sessionId })
        console.log("loadSession response:", response)
        if (response !== null) {
          const session = response.gameSession || {}
          setGameSessionData(session)

          const gameId = session.gameId
          const gameDetails = await fetchGameById(gameId)
          setCurrentGame(gameDetails)

          const processed = selectProcessor(
            session?.gameId,
            session?.contents || session?.gameSessionContents
          )
          setExercisesContent(processed)

          // find current participant
          const currentPlayerObj = session?.participants?.find(
            (p) => p.userId === userData?.userId
          )
          if (currentPlayerObj) {
            const answersFromPlayer = currentPlayerObj.answers || []
            const formatted = formatOnlyAnswered(answersFromPlayer, processed)
            setAnswers(formatted)
          } else {
            setAnswers([])
          }

          const currentGameSessionContentId =
            session?.currentGameSessionContentId
          const currentIndex = processed.findIndex(
            (content) =>
              content.gameSessionContentId === currentGameSessionContentId
          )
          if (currentIndex >= 0) {
            setExerciseContentIndex(currentIndex)
          } else {
            setExerciseContentIndex(0)
          }
          return {
            gameSessionData: session,
            game: gameDetails,
            exerciseContentIndex: currentIndex,
          }
        } else {
          navigate(routes.NEW_GAMES)
        }
      } catch (error) {
        console.error("Error loading session:", error)
        navigate(routes.NEW_GAMES)
      }
    },
    [userData?.userId, formatOnlyAnswered]
  )

  const createGame = useCallback(
    async ({
      gameId,
      moduleId,
      sessionId = 0,
      metadata = "",
      gameMode = CONTENT_REFERENCE_TYPES.MODULE,
    }) => {
      console.log("creating game..." + sessionId)

      // Reset state for the new session
      setCountdownCompleted(false)
      setExerciseContentIndex(0)
      setIsGameFinished(false)
      setAnswers([])

      if (sessionId > 0) {
        const response = await getGameSessionById({ gameSessionId: sessionId })
        console.log("data", response?.gameSession)
        const session = response?.gameSession || {}
        setGameSessionData(session)
        const processed = selectProcessor(session?.gameId, session?.contents)
        setExercisesContent(processed)

        // find current participant and set answers
        const currentPlayerObj = session?.participants?.find(
          (p) => p.userId === userData?.userId
        )
        const answersFromPlayer = currentPlayerObj?.answers || []
        const formatted = formatOnlyAnswered(answersFromPlayer, processed)
        setAnswers(formatted)

        // No start screen should appear: set countdown completed to true
        setCountdownCompleted(true)
        console.log("answersFromPlayer", answersFromPlayer)

        // Find the first unanswered question index
        const firstUnansweredIndex = processed.findIndex(
          (content) =>
            !answersFromPlayer.some(
              (ans) =>
                ans.gameSessionContentId === content.gameSessionContentId ||
                ans.GameSessionContentId === content.gameSessionContentId
            )
        )
        console.log("firstUnansweredIndex", firstUnansweredIndex)
        if (firstUnansweredIndex >= 0) {
          setExerciseContentIndex(firstUnansweredIndex)
        } else {
          // If there are exercises and all are answered, show game results directly
          if (processed.length > 0) {
            setIsGameFinished(true)
          }
        }

        // Load the game details on refresh/deep link
        const currentId = session?.gameId
        if (currentId) {
          const gameDetails = await fetchGameById(currentId)
          if (gameDetails) {
            setCurrentGame(gameDetails)
          }
        }

        return {
          gameSessionData: session,
          exercisesContent: processed,
        }
      } else {
        const data = await setIndividualGameSession({
          gameId,
          moduleId,
          metadata,
          gameContentReferenceTypeId: gameMode,
          learningLanguageId: selectedGroupData?.learningLanguageId,
          ...(gameId === 3 && {
            gameContentReferenceTypeId: CONTENT_REFERENCE_TYPES.LEVEL,
          }),
        })
        if (!data?.contents || data?.contents?.length === 0) {
          showAlert("No content available")
          navigate(routes.NEW_GAMES)
          return null
        }
        console.log("data", data)
        setGameSessionData(data)
        const processed = selectProcessor(gameId, data?.contents)
        setExercisesContent(processed)
        return {
          gameSessionData: data,
          exercisesContent: processed,
        }
      }
    },
    [
      setIndividualGameSession,
      fetchGameById,
      selectedGroupData?.groupId,
      userData?.userId,
      formatOnlyAnswered,
    ]
  )

  const setNextContent = useCallback(async () => {
    const nextIndex = exerciseContentIndex + 1
    console.log({ exerciseContentIndex, total: exercisesContent.length })
    if (nextIndex < exercisesContent.length) {
      setExerciseContentIndex((prevIndex) => prevIndex + 1)
    } else {
      const { gameSessionId, participants } = gameSessionData
      const participantId =
        participants?.find((p) => p.userId === userData?.userId)
          ?.gameSessionParticipantId || -1
      await setSessionActivityLog({
        gameSessionContentId: 0,
        viewId: getScreenId("games"),
      })
      await setGameSessionStatus({
        gameSessionId,
        gameSessionParticipantId: participantId,
        gameSessionStatusId: 4, // finished
      })
      setTimeout(() => {
        setIsGameFinished(true)
      }, 1000)
    }
    setIsLoadingGameContent(false)
  }, [exerciseContentIndex, exercisesContent, gameSessionData, userData])

  const sendAnswer = useCallback(
    async ({
      gameSessionContentId,
      isCorrect,
      grade = 0,
      answers = [],
      metadata = "",
      extraData = {},
      autoSlide = true,
      files = [],
      playSound = true,
    }) => {
      console.log("sendAnswer", {
        gameSessionContentId,
        isCorrect,
        grade,
        answers,
        metadata,
        files,
      })
      const { gameSessionId, participants } = gameSessionData
      // find current participant
      const currentPlayer = participants?.find(
        (p) => p.userId === userData?.userId
      )
      const participantId = currentPlayer?.gameSessionParticipantId || -1
      if (playSound) {
        if (playSound === "good" || (playSound === true && isCorrect)) {
          playGoodAnswer()
        } else if (
          playSound === "wrong" ||
          (playSound === true && !isCorrect)
        ) {
          playWrongAnswer()
        }
      }
      try {
        // set answers for current content
        const response = await setGameSessionAnswer({
          gameId: currentGame?.id,
          gameSessionId,
          gameSessionParticipantId: participantId,
          gameSessionContentId,
          isCorrect,
          grade,
          answers,
          metadata,
          files,
        })
        console.log({ response })
        console.log(response?.status, "response status")
        if (!response?.status) return
        const answer = response?.data?.answer
        const currentExercise = exercisesContent[exerciseContentIndex]
        const correctSentence = currentExercise?.correctSentence || ""
        let playerAnswer = ""
        console.log(currentExercise, "currentExercise")
        if (currentExercise?.options?.length > 1) {
          const optionAnswer =
            answer.answer.length > 0 ? answer.answer[0] : null
          console.log(optionAnswer, "optionAnswer")
          if (optionAnswer) {
            const playerSelectedOption = currentExercise?.options?.find(
              (opt) =>
                opt.gameSessionContentId === optionAnswer.GameSessionContentId
            )
            playerAnswer = playerSelectedOption?.text || ""
          }
        } else {
          const optionAnswer =
            answer.answer.length > 0 ? answer.answer[0] : null
          if (typeof optionAnswer.Answer === "string") {
            playerAnswer = optionAnswer.Answer
          }
        }

        const newAnswer = {
          ...answer,
          correctAnswer: correctSentence,
          audioURL: currentExercise?.audioURL,
          userSelected: playerAnswer,
          isCorrect: answer?.isCorrect || false,
          explanation:
            typeof currentExercise?.explanation === "string"
              ? currentExercise?.explanation
              : currentExercise?.explanation?.text || "",
          ...extraData, // extraData fields override defaults when present
        }

        setAnswers((prev) => [...prev, newAnswer])
        const currentExerciseIndex = exerciseContentIndex
        console.log(exerciseContentIndex, "currentExerciseIndex")
        if (currentPlayer) {
          setGameSessionData((prevSessionData) => {
            const prevParticipants = prevSessionData?.participants || []
            const updatedParticipants = prevParticipants.map((p) =>
              p.userId === userData?.userId
                ? { ...p, points: p.points + answer.points }
                : p
            )
            return {
              ...prevSessionData,
              participants: updatedParticipants,
            }
          })
        }
        if (response?.status) {
          // update next content for current game session

          if (autoSlide && currentExerciseIndex >= 0) {
            const nextIndex = currentExerciseIndex + 1
            console.log(nextIndex, "nextIndex")
            if (nextIndex < exercisesContent.length) {
              const nextContentId =
                exercisesContent[nextIndex].gameSessionContentId
              console.log(nextContentId, "nextContentId")
              await setGameSessionNextContent({
                gameSessionContentId: nextContentId,
                gameSessionId,
                gameId: currentGame?.id,
                gameSessionParticipantId: participantId,
              })
            }
          }
        }
        const isLastAnswer =
          currentExerciseIndex === exercisesContent.length - 1
        console.log({ isLastAnswer, autoSlide })
        if (autoSlide && isCorrect && !isLastAnswer) {
          // wait 1 second and go to next exercise
          setTimeout(() => {
            setIsLoadingGameContent(true)
            setNextContent()
          }, 1000)
        }

        if (autoSlide && isCorrect && isLastAnswer) {
          await setGameSessionStatus({
            gameSessionId,
            gameSessionParticipantId: participantId,
            gameSessionStatusId: 4, // finished
          })
          setTimeout(() => {
            setIsGameFinished(true)
          }, 1000)
        }
      } catch (error) {
        console.log("[sendAnswer error]", error)
        alert(error)
      }
    },
    [
      currentGame,
      gameSessionData,
      userData?.userId,
      exercisesContent,
      exerciseContentIndex,
    ]
  )

  const loadGameContent = useCallback(
    async ({ isRetry = false }) => {
      try {
        const queryParams = new URLSearchParams(location.search)
        const sessionId = queryParams.get("sessionId")
        const isResuming = sessionId !== null && sessionId !== "" && !isRetry

        if (!isResuming) {
          setExerciseContentIndex(0)
          setIsGameFinished(false)
          setAnswers([])
        }
        setIsLoadingGameContent(true)
        if (isRetry) {
          setCountdownCompleted(false)
          const data = await retryIndividualGameSession({
            gameSessionId: gameSessionData?.gameSessionId,
          })
          console.log("data", data)
          setGameSessionData(data)
          const processed = selectProcessor(data?.gameId, data?.contents)
          setExercisesContent(processed)
          setExerciseContentIndex(0)
          setIsLoadingGameContent(false)
          // // change param query to current url
          setSearchParams(
            {
              ...Object.fromEntries([...searchParams]),
              sessionId: data?.gameSessionId,
            },
            { replace: true }
          )
          return processed[0]
        }
        console.log(
          "loading game content, ",
          gameSessionData !== null,
          gameSessionData
        )
        if (gameSessionData !== null) {
          console.log("get game", exercisesContent)
          const exercises = exercisesContent || []
          setIsLoadingGameContent(false)
          if (exercises?.length > 0) {
            return exercises[exerciseContentIndex]
          }
        } else {
          // create game
          console.log("create game")
          if (!sessionId) {
            setIsLoadingGameContent(false)
            navigate(routes.NEW_GAMES)
          } else {
            const response = await createGame({ sessionId })
            const exercises = response?.exercisesContent || []
            setIsLoadingGameContent(false)
            if (exercises?.length > 0) {
              const player = response?.gameSessionData?.participants?.find(
                (p) => p.userId === userData?.userId
              )
              const answersFromPlayer = player?.answers || []
              const firstUnansweredIndex = exercises.findIndex(
                (content) =>
                  !answersFromPlayer.some(
                    (ans) =>
                      ans.gameSessionContentId ===
                        content.gameSessionContentId ||
                      ans.GameSessionContentId === content.gameSessionContentId
                  )
              )
              const startIndex =
                firstUnansweredIndex >= 0 ? firstUnansweredIndex : 0
              return exercises[startIndex]
            }
            return {} // no data for current exercise
          }
        }
        setIsLoadingGameContent(false)
      } catch (error) {
        console.log("[loadGameContent error]", error)
        setIsLoadingGameContent(false)
      }
    },
    [
      gameSessionData,
      exercisesContent,
      searchParams,
      selectedGroupData?.groupId,
      exerciseContentIndex,
      userData?.userId,
      createGame,
    ]
  )

  const gameAnswers = useMemo(() => {
    if (isGameFinished) {
      return formatPlayerAnswers(answers, exercisesContent)
    }
    return answers
  }, [isGameFinished, answers, exercisesContent, formatPlayerAnswers])

  const value = useMemo(
    () => ({
      game: currentGame,
      level: {},
      exercisesContent,
      gameSessionData,
      gameAnswers,
      exerciseContentIndex,
      isLoadingGameContent,
      isGameFinished,
      gameTime: accountType < 7 ? 30 : 20, // elementary 30s, secondary and university 20s
      countdownCompleted,
      playerStats,
      setGame: setCurrentGame,
      createGame,
      sendAnswer,
      loadGameSession: loadSession,
      setNextContent,
      loadGameContent,
      setCountdownCompleted,
      setPlayerStats,
      setIsLoadingGameContent,
    }),
    [
      currentGame,
      exercisesContent,
      gameSessionData,
      gameAnswers,
      userData?.userId,
      exerciseContentIndex,
      isLoadingGameContent,
      searchParams,
      isGameFinished,
      countdownCompleted,
      selectedGroupData?.groupId,
      playerStats,
    ]
  )

  const isPlayingGame = useMemo(() => {
    const path = location.pathname.toLowerCase()
    const normalizedPath = path.endsWith("/") ? path.slice(0, -1) : path
    const isGamesList = normalizedPath.endsWith(routes.NEW_GAMES)
    return normalizedPath.includes(routes.NEW_GAMES) && !isGamesList
  }, [location.pathname])

  return (
    <GamesContext.Provider value={value}>
      <Box sx={sx} className={"games-context"}>
        {children}
        {isPlayingGame && !countdownCompleted && currentGame && (
          <StartGame
            game={currentGame}
            onComplete={() => setCountdownCompleted(true)}
          />
        )}
      </Box>
    </GamesContext.Provider>
  )
}
export default GamesProvider
